import { useEffect, useMemo, useRef } from 'react';
import clsx from 'clsx';

import styles from './Trustpilot.module.css';

const TRUSTPILOT_SCRIPT_ID = 'trustpilot-widget-bootstrap';
const TRUSTPILOT_SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

interface TrustpilotProps {
  templateId: string;
  businessUnitId: string;
  theme: 'light' | 'dark';
  locale?: string;
  className?: string;
}

export default function Trustpilot({
  templateId,
  businessUnitId,
  theme,
  locale: baseLocale = 'de-DE',
  className,
}: TrustpilotProps) {
  const ref = useRef<HTMLDivElement>(null);

  const { locale, language } = useMemo(
    () => ({
      locale: baseLocale.replace('_', '-'),
      language: baseLocale.substring(0, 2),
    }),
    [baseLocale],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const init = () => window.Trustpilot!.loadFromElement(element, true);

    let script = document.getElementById(TRUSTPILOT_SCRIPT_ID) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = TRUSTPILOT_SCRIPT_ID;
      script.async = true;
      script.src = TRUSTPILOT_SCRIPT_SRC;
      document.head.appendChild(script);
    }

    if (window.Trustpilot) {
      init();
      return;
    }

    script.addEventListener('load', init);
    return () => script.removeEventListener('load', init);
  }, []);

  return (
    <div
      ref={ref}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={clsx('trustpilot-widget', styles.trustpilot, className)}
      data-locale={locale}
      data-template-id={templateId}
      data-businessunit-id={businessUnitId}
      data-style-height="auto"
      data-style-width="100%"
      data-theme={theme}
      data-tags="Website"
      data-stars="4,5"
      data-review-languages={language}
    />
  );
}
